/**
 * Workspace-name hook + helpers — single source of truth for the
 * platform-shell's first breadcrumb segment.
 *
 * Persisted in localStorage at `ipaas:workspace:name`. Defaults to
 * "Workspace". Subscribers (Topbar + Settings panel) sync via the
 * `ipaas:workspace-changed` event so renaming in Settings updates
 * the breadcrumb without a reload, and via `storage` so a rename in
 * one tab updates the other.
 *
 * v1 keeps this client-only — the workspace name is just a display
 * label, not a multi-tenant boundary. If we ever go multi-tenant
 * we'll move it server-side; for now localStorage is the right
 * level of complexity.
 */
import { useEffect, useState } from 'react';

export const WORKSPACE_NAME_KEY = 'pipebase:workspace:name';
const DEFAULT_NAME = 'Workspace';
const CHANGE_EVENT = 'pipebase:workspace-changed';

export function getWorkspaceName(): string {
  try {
    const v = localStorage.getItem(WORKSPACE_NAME_KEY);
    return v && v.trim().length > 0 ? v : DEFAULT_NAME;
  } catch {
    return DEFAULT_NAME;
  }
}

export function setWorkspaceNamePersisted(name: string): void {
  try {
    const trimmed = name.trim().slice(0, 60);
    if (trimmed.length === 0) localStorage.removeItem(WORKSPACE_NAME_KEY);
    else localStorage.setItem(WORKSPACE_NAME_KEY, trimmed);
    window.dispatchEvent(new Event(CHANGE_EVENT));
  } catch {
    /* localStorage unavailable — silent no-op */
  }
}

/** Subscribe to the workspace name. Re-renders when it changes
 *  (in this tab via custom event, across tabs via `storage`). */
export function useWorkspaceName(): string {
  const [value, setValue] = useState<string>(getWorkspaceName);
  useEffect(() => {
    const sync = () => setValue(getWorkspaceName());
    window.addEventListener(CHANGE_EVENT, sync);
    const onStorage = (e: StorageEvent) => {
      if (e.key === WORKSPACE_NAME_KEY) sync();
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener('storage', onStorage);
    };
  }, []);
  return value;
}
